import { AlertBox } from '@components/ui';
import type { GlobalInsight, UserInsights } from '@hooks/useInsights';
import type { ReactElement } from 'react';
import { GlobalInsightCard } from './GlobalInsightCard';

interface GeneralInsightsProps {
  globalInsights: GlobalInsight[];
  insights: UserInsights | null;
}

export function GeneralInsights({ globalInsights, insights }: GeneralInsightsProps): ReactElement {
  const totalBets = insights?.totalBets || 0;

  // If global insights are loaded from DB, render them
  if (globalInsights && globalInsights.length > 0) {
    return (
      <div className="border-t border-theme-border pt-6">
        <h2 className="text-lg font-bold mb-3 text-white">General Betting Insights</h2>
        <div className="space-y-4">
          {globalInsights.map((insight) => (
            <GlobalInsightCard key={insight.id} insight={insight} totalBets={totalBets} />
          ))}
        </div>
      </div>
    );
  }

  // Fallback to hardcoded content if DB is empty
  return (
    <div className="border-t border-theme-border pt-6">
      <h2 className="text-lg font-bold mb-3 text-white">General Betting Insights</h2>
      <div className="space-y-4">
        <AlertBox variant="blue" title="Parlay Win Probability (Industry Data)">
          <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-3 text-sm">
            <div className="bg-theme-surface rounded p-2 text-center">
              <div className="text-xl font-bold text-white">25%</div>
              <div className="text-theme-text-tertiary">2-leg parlay</div>
            </div>
            <div className="bg-theme-surface rounded p-2 text-center">
              <div className="text-xl font-bold text-white">12.5%</div>
              <div className="text-theme-text-tertiary">3-leg parlay</div>
            </div>
            <div className="bg-theme-surface rounded p-2 text-center">
              <div className="text-xl font-bold text-white">6.25%</div>
              <div className="text-theme-text-tertiary">4-leg parlay</div>
            </div>
            <div className="bg-theme-surface rounded p-2 text-center">
              <div className="text-xl font-bold text-theme-danger">~1%</div>
              <div className="text-theme-text-tertiary">6+ leg parlay</div>
            </div>
          </div>
          <p className="text-theme-text-tertiary text-xs mt-3">
            *Assumes 50% win rate per leg. Actual rates vary based on odds and correlation.
          </p>
        </AlertBox>

        <AlertBox variant="purple" title="The House Edge (Vig/Juice)">
          <ul className="space-y-2 text-sm">
            <li>
              <strong>Standard -110/-110 lines:</strong> Sportsbook takes ~4.5% on every bet.
            </li>
            <li>
              <strong>Parlays compound the vig:</strong> A 4-leg parlay has ~17% house edge built
              in.
            </li>
            <li>
              <strong>Same Game Parlays:</strong> Often have 20-30% house edge due to correlated
              odds manipulation.
            </li>
            <li>
              <strong>To break even long-term:</strong> You need to win ~52.4% of -110 bets.
            </li>
          </ul>
        </AlertBox>

        <AlertBox variant="gray" title="Sharp Bettor Benchmarks">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-theme-warning">54-56%</div>
              <div className="text-theme-text-tertiary">Win rate target</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-theme-success">3-8%</div>
              <div className="text-theme-text-tertiary">Annual ROI (pros)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-theme-primary">1000+</div>
              <div className="text-theme-text-tertiary">Bets for valid sample</div>
            </div>
          </div>
          {totalBets > 0 && totalBets < 100 && (
            <p className="text-theme-text-tertiary text-xs mt-3">
              Your {totalBets} bets is a small sample. Keep tracking for reliable conclusions!
            </p>
          )}
        </AlertBox>
      </div>
    </div>
  );
}
